import React, { memo, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { CollectedDataDocument } from 'src/interfaces/collectedData'
import { DownloadStepDocument } from 'src/constants/actionTypes'
import { getMimeTypeFromExtension, downloadFileFromBuffer } from 'src/utils/fileDownload'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'

interface Props {
  document: CollectedDataDocument
  partnerDriverId: string
  stepId: string
}

const DownloadAllButtonComponent = ({ document, partnerDriverId, stepId }: Props) => {
  const dispatch = useDispatch()
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadAll = useCallback(async () => {
    try {
      setIsDownloading(true)

      const buffer: ArrayBuffer = await createAsyncAction(
        dispatch,
        DownloadStepDocument.request({
          partnerDriverId,
          stepId,
          documentId: document.id,
        }),
      )

      const fileExtension = document.extension || 'pdf'
      const mimeType = getMimeTypeFromExtension(fileExtension)

      downloadFileFromBuffer(buffer, document.fileName, mimeType)
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsDownloading(false)
    }
  }, [dispatch, partnerDriverId, stepId, document])

  return (
    <Button
      size="small"
      color="light"
      cardAction
      rightIcon={
        <GSIcon
          iconName="gs_icon_circle_arrow_down"
          width={20}
          css={{ '& path': { fill: colors.PRIMARY_PURPLE_500 } }}
        />
      }
      onClick={handleDownloadAll}
      loading={isDownloading}
      className={tw`w-full [& > div]:ml-1`}
    >
      Download All
    </Button>
  )
}

export const DownloadAllButton = memo(DownloadAllButtonComponent)
